/**
 * The live playback levels, read from the preview's audio graph every frame while it plays and drawn by the meters beside the
 * timeline. A plain shared object, not React state: the meters read it in their own animation frame.
 */
export interface LiveLevels { /** 0..1 on the meter scale (−48 dBFS .. 0), the loudness of the moment */ voice: number; bed: number; /** the loudest moment lately, held briefly */ voicePeak: number; bedPeak: number; /** when the levels were last written (ms); meters fall silent after a while without news */ at: number }
export const liveLevels: LiveLevels = { voice: 0, bed: 0, voicePeak: 0, bedPeak: 0, at: 0 };
if (typeof window !== "undefined") (window as unknown as { __studioLevels: LiveLevels }).__studioLevels = liveLevels;

const FLOOR_DB = -48;
/** A signal's RMS on the meter scale: 0 at −48 dBFS and below, 1 at full scale. */
export const meterOf = (rms: number): number => (rms <= 0 ? 0 : Math.max(0, Math.min(1, (20 * Math.log10(rms) - FLOOR_DB) / -FLOOR_DB)));

const HOLD_MS = 1200;
const holds = { voice: 0, bed: 0 };
/** Writes one channel's level: the peak is held for a moment, then follows the level down. */
export function pushLevel(which: "voice" | "bed", rms: number, now = performance.now()): void {
  const v = meterOf(rms);
  const peakKey = which === "voice" ? "voicePeak" : "bedPeak";
  liveLevels[which] = v;
  if (v >= liveLevels[peakKey]) { liveLevels[peakKey] = v; holds[which] = now; }
  else if (now - holds[which] > HOLD_MS) liveLevels[peakKey] = Math.max(v, liveLevels[peakKey] - 0.012);
  liveLevels.at = now;
}

/** RMS of a time-domain buffer (samples in −1..1). */
export function rmsOf(buf: Float32Array): number {
  let s = 0;
  for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
  return Math.sqrt(s / Math.max(1, buf.length));
}
